import { RSAA } from 'redux-api-middleware';
import differenceInHours from 'date-fns/differenceInHours';
import uniqWith from 'lodash.uniqwith';
import isEqual from 'lodash.isequal';

import customFailure from 'helpers/customFailureAction';
import { apiTypeToEntityType } from 'helpers/mapEntityTypes';
import getEntityMetadataURL from 'url-constructors/getEntityMetadataURL';
import { EntityTypes } from 'context/entityContext';
import { TDispatchResult, TGetRootState } from 'types/shared';
import { Dispatch } from 'redux';

export const LOAD_ENTITY_REQUEST = 'opus/compare/LOAD_ENTITY_REQUEST';
export const LOAD_ENTITY_SUCCESS = 'opus/compare/LOAD_ENTITY_SUCCESS';
export const LOAD_ENTITY_FAILURE = 'opus/compare/LOAD_ENTITY_FAILURE';
export const ADD_ENTITIES = 'opus/compare/ADD_ENTITIES';

type EntityActionTypes =
  | typeof LOAD_ENTITY_SUCCESS
  | typeof LOAD_ENTITY_REQUEST
  | typeof LOAD_ENTITY_FAILURE
  | typeof ADD_ENTITIES;

interface IEntityAction {
  type?: EntityActionTypes;
  payload?: any;
}

interface IEntityState {
  byId: any;
  allIds: any[];
}

export const initialState: IEntityState = {
  byId: {},
  allIds: [],
};

export default function reducer(
  state: IEntityState = initialState,
  action: IEntityAction = {}
): IEntityState {
  switch (action.type) {
    case LOAD_ENTITY_REQUEST: {
      const { id, type } = action.payload;
      const entityType = apiTypeToEntityType(type);

      return {
        ...state,
        byId: {
          ...state.byId,
          [entityType]: {
            ...state.byId[entityType],
            [id]: {
              loading: true,
              error: false,
              entity: state.byId[entityType]?.[id]?.entity ?? null,
              metadata: null,
            },
          },
        },
        allIds: uniqWith([...state.allIds, { id, type: entityType }], isEqual),
      };
    }

    case LOAD_ENTITY_SUCCESS: {
      const { entity, metadata } = action.payload;
      const type = apiTypeToEntityType(entity.type);

      return {
        ...state,
        byId: {
          ...state.byId,
          [type]: {
            ...state.byId[type],
            [entity.id]: {
              ...state.byId[type][entity.id],
              entity,
              metadata,
              loading: false,
              error: false,
              lastUpdate: new Date().toISOString(),
            },
          },
        },
      };
    }

    case LOAD_ENTITY_FAILURE: {
      const { type, id, error } = action.payload;
      const entityType = apiTypeToEntityType(type);

      return {
        ...state,
        byId: {
          ...state.byId,
          [entityType]: {
            ...state.byId[entityType],
            [id]: {
              entity: null,
              metadata: null,
              loading: false,
              error: new Error(error.error),
              errorInfo: error.errorInfo,
              status: error.status,
            },
          },
        },
      };
    }

    case ADD_ENTITIES: {
      const { byId, allIds, type } = action.payload;
      const entityType = apiTypeToEntityType(type);

      return {
        ...state,
        byId: {
          ...state.byId,
          [entityType]: {
            ...state.byId[entityType],
            ...byId,
          },
        },
        allIds: uniqWith([...state.allIds, ...allIds], isEqual),
      };
    }

    default: {
      return state;
    }
  }
}

/**
 * Checks if the entity is already cached in redux state
 * @export
 * @param {string} type e.g. track/release/artist/label/project
 * @param {string} id   e.g. 1000201
 * @returns boolean
 */
export function shouldLoadEntity(
  type: EntityTypes,
  id: string,
  shouldHaveMeta = false
): TDispatchResult<boolean> {
  return (_: Dispatch, getState: TGetRootState): boolean => {
    const store = getState();
    const isCachedEntity = store.entity.allIds.find((el: any) => isEqual(el, { id, type }))
    const shouldUpdateMetadata = shouldHaveMeta && !isCachedEntity?.metadata

    // If the entity is not in cache yet, load it without conditions
    if (!isCachedEntity || shouldUpdateMetadata) {
      return true;
    }

    // Do not load the entity if we are already loading it
    if (store.entity.byId[type][id].loading) return false;

    // Calculate the amount of hours elapsed between the last data refresh and the current time
    // If more than 1 hour has passed, we want to reload the entity
    const difference = differenceInHours(
      new Date(),
      new Date(store.entity.byId[type][id].lastUpdate)
    );

    return difference > 1;
  };
}

export const loadEntity = (
  entityType: EntityTypes,
  entityId: string,
  loadMetadata: boolean
): any => {
  const url = getEntityMetadataURL(entityType, entityId, {
    expand: loadMetadata ? 'metadata' : 'none',
  });

  return {
    [RSAA]: {
      types: [
        {
          type: LOAD_ENTITY_REQUEST,
          payload: {
            type: entityType,
            id: entityId,
          },
        },
        LOAD_ENTITY_SUCCESS,
        {
          type: LOAD_ENTITY_FAILURE,
          payload: async (
            action: IEntityAction,
            state: IEntityState,
            res: any
          ): Promise<any> => {
            const error = await customFailure(res);
            return {
              error,
              type: entityType,
              id: entityId,
            };
          },
        },
      ],
      endpoint: url,
      method: 'GET',
      credentials: 'same-origin',
    },
  };
};

/** Adds a list of entity data into the entity reducer */
export const addEntities = (entityType: EntityTypes, entities: any[]): any => {
  const byId = entities.reduce((obj, { entity, metadata }) => {
    return {
      ...obj,
      [entity.id]: {
        entity,
        metadata,
        loading: false,
        error: false,
        lastUpdate: new Date().toISOString(),
      },
    };
  }, {});

  const allIds = entities.map(({ entity }) => ({
    id: entity.id,
    type: entityType,
  }));

  return {
    type: ADD_ENTITIES,
    payload: { byId, allIds, type: entityType },
  };
};

export const loadEntityData = (entity: any): any => {
  return {
    type: LOAD_ENTITY_SUCCESS,
    payload: { entity },
  };
};
